import { useEffect, useRef, useState } from 'react'
import type { PhoenixEventHandler } from './usePhoenixChannel'

/**
 * Live captions for every participant, server-sourced — the only source of
 * captions. CaptionsOverlay's browser Web Speech API fallback is gone, and the
 * captions controls are offered only in a meeting with transcription.
 *
 * No second connection to receive these: the transcription server pushes a
 * finalized segment to signaling's existing generic room broadcast
 * (POST /api/rooms/:room_id/broadcast, the same relay the H.323/SIP bridges
 * use), which fans it out over the room's Phoenix channel — reaching every
 * participant, including the speaker, the same way useActiveSpeakers already
 * consumes a different broadcast on the same channel.
 *
 *     "transcription:final" => {
 *       peerId, speakerName, text, confidence, ts
 *     }
 */

export interface LiveCaptionEntry {
  id: string
  peerId: string
  speaker: string
  text: string
  ts: number
}

/** Caption entries older than this are dropped — matches the old overlay's silence-clear. */
const ENTRY_TTL_MS = 6000
/** Only the most recent few are shown at once, same as the overlay used to cap at 3. */
const MAX_ENTRIES = 3

export function useLiveCaptions(
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void,
): LiveCaptionEntry[] {
  const [entries, setEntries] = useState<LiveCaptionEntry[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const scheduleClear = () => {
      clearTimer()
      timerRef.current = setTimeout(() => setEntries([]), ENTRY_TTL_MS)
    }

    const off = subscribe('transcription:final', (payload) => {
      const peerId = typeof payload.peerId === 'string' ? payload.peerId : null
      const text = typeof payload.text === 'string' ? payload.text.trim() : ''
      if (!peerId || !text) return

      const entry: LiveCaptionEntry = {
        id: `${peerId}-${Date.now()}`,
        peerId,
        speaker: typeof payload.speakerName === 'string' && payload.speakerName ? payload.speakerName : peerId,
        text,
        ts: typeof payload.ts === 'number' ? payload.ts : Date.now(),
      }

      setEntries((prev) => [...prev, entry].slice(-MAX_ENTRIES))
      scheduleClear()
    })

    return () => {
      off()
      clearTimer()
    }
  }, [subscribe])

  return entries
}
